import React, { useState, useEffect, useRef } from 'react';
import './TeacherMonitorDashboard.css';
import { buildWebSocketUrl } from './config';

const TeacherMonitorDashboard = () => {
    const [teacherId, setTeacherId] = useState('');
    const [password, setPassword] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [teacherInfo, setTeacherInfo] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [websocket, setWebsocket] = useState(null);
    const [error, setError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [screenData, setScreenData] = useState({});
    
    const screensRef = useRef({});

    // Available teachers for demo
    const availableTeachers = [
        { id: 'math_teacher', name: 'Math Teacher', subject: 'Mathematics' },
        { id: 'english_teacher', name: 'English Teacher', subject: 'English Language' },
        { id: 'science_teacher', name: 'Science Teacher', subject: 'Science' },
        { id: 'history_teacher', name: 'History Teacher', subject: 'History' }
    ];

    const login = async () => {
        if (!teacherId || !password) {
            setError('Please enter teacher ID and password');
            return;
        }

        try {
            const response = await fetch('/api/teacher/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    teacher_id: teacherId,
                    password: password
                })
            });

            const data = await response.json();

            if (data.success) {
                setTeacherInfo(data);
                setIsAuthenticated(true);
                setError('');
                
                // Connect to monitoring WebSocket
                connectToMonitoring();
                
                // Load existing session data
                loadTeacherSession();
            } else {
                setError('Invalid teacher credentials');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Login failed. Please try again.');
        }
    };

    const connectToMonitoring = () => {
        const wsUrl = `${buildWebSocketUrl()}/ws/teacher-monitor/${teacherId}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            console.log('✅ Teacher monitoring connected');
            setConnectionStatus('connected');
            setWebsocket(ws);
            
            // Request current student list
            ws.send(JSON.stringify({ type: 'get_students' }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                switch (data.type) {
                    case 'student_list':
                        setStudents(data.students || []);
                        console.log('📋 Updated student list:', data.students?.length || 0, 'students');
                        break;
                        
                    case 'student_connected':
                        console.log('👨‍🎓 Student connected:', data.student_name);
                        // Refresh student list
                        ws.send(JSON.stringify({ type: 'get_students' }));
                        break;
                        
                    case 'student_disconnected':
                        console.log('👋 Student disconnected:', data.student_id);
                        // Remove from screen data and refresh list
                        setScreenData(prev => {
                            const updated = { ...prev };
                            delete updated[data.student_id];
                            return updated;
                        });
                        ws.send(JSON.stringify({ type: 'get_students' }));
                        break;
                        
                    case 'screen_update':
                        // Update screen data for student
                        setScreenData(prev => ({
                            ...prev,
                            [data.student_id]: {
                                data: data.data,
                                timestamp: data.timestamp
                            }
                        }));
                        break;
                        
                    case 'pong':
                        // Heartbeat response
                        break;
                        
                    default:
                        console.log('Unknown message type:', data.type);
                }
            } catch (error) {
                console.error('WebSocket message error:', error);
            }
        };

        ws.onclose = () => {
            console.log('👋 Teacher monitoring disconnected');
            setConnectionStatus('disconnected');
            setWebsocket(null);
        };

        ws.onerror = (error) => {
            console.error('❌ Teacher monitoring WebSocket error:', error);
            setError('Connection failed. Please try again.');
            setConnectionStatus('error');
        };
    };

    const loadTeacherSession = async () => {
        try {
            const response = await fetch(`/api/teacher/session/${teacherId}`);
            const data = await response.json();
            
            setStudents(data.students || []);
            console.log('📊 Loaded teacher session:', data.total_students, 'students');
        } catch (error) {
            console.error('Failed to load teacher session:', error);
        }
    };

    const logout = () => {
        if (websocket) {
            websocket.close();
        }
        
        setIsAuthenticated(false);
        setTeacherInfo(null);
        setStudents([]);
        setSelectedStudent(null);
        setScreenData({});
        setTeacherId('');
        setPassword('');
        setError('');
        setConnectionStatus('disconnected');
    };

    const selectStudent = (student) => {
        setSelectedStudent(student);
    };

    const sendHeartbeat = () => {
        if (websocket && websocket.readyState === WebSocket.OPEN) {
            websocket.send(JSON.stringify({ type: 'ping' }));
        }
    };

    // Send heartbeat every 30 seconds
    useEffect(() => {
        if (connectionStatus === 'connected') {
            const interval = setInterval(sendHeartbeat, 30000);
            return () => clearInterval(interval);
        }
    }, [connectionStatus, websocket]);

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected': return '#10B981';
            case 'error': return '#EF4444';
            default: return '#6B7280';
        }
    };

    const getStatusText = () => {
        switch (connectionStatus) {
            case 'connected': return 'Connected';
            case 'error': return 'Connection Error';
            default: return 'Disconnected';
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Never';
        return new Date(timestamp).toLocaleTimeString();
    };

    const getStudentScreenData = (studentId) => {
        return screenData[studentId];
    };

    if (!isAuthenticated) {
        return (
            <div className="teacher-monitor-login">
                <div className="login-container">
                    <h2>🏫 Teacher Monitor Dashboard</h2>
                    <p>Sign in to monitor your students</p>
                    
                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}
                    
                    <div className="login-form">
                        <div className="form-group">
                            <label>Teacher Account:</label>
                            <select
                                value={teacherId}
                                onChange={(e) => setTeacherId(e.target.value)}
                                className="form-select"
                            >
                                <option value="">Select your account...</option>
                                {availableTeachers.map(teacher => (
                                    <option key={teacher.id} value={teacher.id}>
                                        {teacher.name} - {teacher.subject}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Password:</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                className="form-input"
                                onKeyPress={(e) => e.key === 'Enter' && login()}
                            />
                        </div>
                        
                        <button 
                            onClick={login}
                            disabled={!teacherId || !password}
                            className="login-button"
                        >
                            🔐 Sign In
                        </button>
                        
                        <div className="demo-credentials">
                            <h4>Demo Credentials:</h4>
                            <p><strong>math_teacher</strong> → password: <code>math123</code></p>
                            <p><strong>english_teacher</strong> → password: <code>english123</code></p>
                            <p><strong>science_teacher</strong> → password: <code>science123</code></p>
                            <p><strong>history_teacher</strong> → password: <code>history123</code></p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="teacher-monitor-dashboard">
            <div className="dashboard-header">
                <div className="teacher-info">
                    <h2>👨‍🏫 {teacherInfo.teacher_name}</h2>
                    <p>{teacherInfo.subject}</p>
                </div>
                
                <div className="header-controls">
                    <div className="status-indicator">
                        <div 
                            className="status-dot"
                            style={{ backgroundColor: getStatusColor() }}
                        ></div>
                        <span>{getStatusText()}</span>
                    </div>
                    
                    <button onClick={logout} className="logout-button">
                        👋 Logout
                    </button>
                </div>
            </div>
            
            {error && (
                <div className="error-message">
                    ⚠️ {error}
                </div>
            )}
            
            <div className="dashboard-content">
                <div className="students-panel">
                    <div className="panel-header">
                        <h3>📋 Connected Students ({students.length})</h3>
                    </div>
                    
                    <div className="students-list">
                        {students.length === 0 ? (
                            <div className="no-students">
                                <p>👥 No students connected</p>
                                <p>Students can connect at: <code>/student-live-monitor</code></p>
                            </div>
                        ) : (
                            students.map(student => (
                                <div 
                                    key={student.id}
                                    className={`student-item ${selectedStudent?.id === student.id ? 'selected' : ''}`}
                                    onClick={() => selectStudent(student)}
                                >
                                    <div className="student-info">
                                        <h4>{student.name}</h4>
                                        <p>ID: {student.id}</p>
                                        <p>Connected: {formatTimestamp(student.connected_at)}</p>
                                        <p>Last Seen: {formatTimestamp(student.last_seen)}</p>
                                    </div>
                                    
                                    <div className="student-status">
                                        <div className={`status-indicator ${student.status}`}>
                                            {student.status === 'online' ? '🟢' : '🔴'} {student.status}
                                        </div>
                                        
                                        {getStudentScreenData(student.id) && (
                                            <div className="screen-indicator">
                                                📱 Screen Active
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                
                <div className="monitor-panel">
                    <div className="panel-header">
                        <h3>📺 Student Screen Monitor</h3>
                        {selectedStudent && (
                            <span>Viewing: {selectedStudent.name}</span>
                        )}
                    </div>
                    
                    <div className="screen-display">
                        {!selectedStudent ? (
                            <div className="no-selection">
                                <p>👆 Select a student to view their screen</p>
                            </div>
                        ) : (
                            <div className="student-screen">
                                {getStudentScreenData(selectedStudent.id) ? (
                                    <div className="screen-container">
                                        <img 
                                            src={getStudentScreenData(selectedStudent.id).data}
                                            alt={`${selectedStudent.name}'s screen`}
                                            className="screen-image"
                                        />
                                        <div className="screen-info">
                                            <p>Last Update: {formatTimestamp(getStudentScreenData(selectedStudent.id).timestamp)}</p>
                                            <p>Student: {selectedStudent.name}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-screen-data">
                                        <p>📱 No screen data available</p>
                                        <p>Student needs to start screen sharing</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="dashboard-footer">
                <div className="connection-info">
                    <p>🔗 WebSocket Status: {getStatusText()}</p>
                    <p>👥 Active Students: {students.length}</p>
                    <p>📱 Screens Sharing: {Object.keys(screenData).length}</p>
                </div>
            </div>
        </div>
    );
};

export default TeacherMonitorDashboard;